import type React from 'react';
import type { RefObject, ChangeEvent, FocusEvent } from 'react';
import styled from 'styled-components';

export type FieldState = 'error' | 'disabled' | 'readonly' | 'default';

export interface TextFieldProps {
  placeholder?: string;
  'aria-label'?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  fieldstate?: FieldState;
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';
  value?: string | number;
  icon?: JSX.Element;
  iconPosition?: 'left' | 'right';
  inputRef?: RefObject<HTMLInputElement>;
  onTypeToggle?: () => void;
}

type StyledInputProps = {
  // Transient props keep styled-only state off the DOM and avoid styled-components unknown-prop warnings.
  $fieldState: FieldState;
  $iconPosition?: TextFieldProps['iconPosition'];
};

type IconProps = {
  $iconPosition?: TextFieldProps['iconPosition'];
};

const IconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: auto;
  overflow: hidden;
  min-width: 12.5rem;
  margin: 0.25rem 0 0 0;
`;

const StyledInput = styled.input<StyledInputProps>`
  ${({ theme: { spacing, typography, colors, media }, $fieldState, $iconPosition }) => {
    return `
      display: flex;
      font-family: ${typography.poppins};
      font-weight: ${typography.light};
      outline: none;
      font-size: ${typography.small};
      line-height: ${typography.lineHeight};
      margin: 0.5rem 0;
      border: .0625rem solid ${colors.black};
      padding-top: ${spacing(3)};
      padding-bottom: ${spacing(3)};
      padding-left: ${$iconPosition === 'left' ? spacing(10) : spacing(4)};
      padding-right: ${$iconPosition === 'right' ? spacing(10) : spacing(4)};
      border-radius: ${spacing(2)};

      &::placeholder {
        color: ${colors.knop};
      }

      &:focus {
        border: .125rem solid ${colors.focoltone};
      }

      &:active {
        border: .0625rem solid ${colors.pascol};
      }

      // Style for error state
      ${
        $fieldState === 'error' &&
        `
        color: ${colors.firebreathingRed};
        border: .125rem solid ${colors.firebreathingRed};
      `
      }

      // Style for disabled state
      ${
        $fieldState === 'disabled' &&
        `
        color: ${colors.knop};
      `
      }

      ${
        $fieldState === 'readonly' &&
        `
        cursor: not-allowed;
        background: ${colors.veryGray};
        color: ${colors.blackSwan};
      `
      }

      ${media.medium(
        () => `
        line-height: ${spacing(4)};
      `,
      )}

      ${media.large(
        () => `
        line-height: ${spacing(4.5)};
      `,
      )}
    `;
  }}
`;

const Icon = styled.div<IconProps>`
  ${({ theme: { spacing }, $iconPosition }) => {
    return `
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 0 ${spacing(3)};
      ${
        $iconPosition === 'left'
          ? `
          left: 0;
        `
          : `
          right: 0;
        `
      }

      img {
        max-width: 12.5rem;
      }
    `;
  }}
`;

export const TextField: React.FC<TextFieldProps> = ({
  placeholder,
  'aria-label': ariaLabel,
  fieldstate = 'default',
  onChange,
  onFocus,
  onBlur,
  type = 'text',
  value,
  icon,
  iconPosition,
  inputRef,
  onTypeToggle,
}) => {
  return (
    <IconWrapper>
      {icon && (
        <Icon $iconPosition={iconPosition} onClick={onTypeToggle}>
          {icon}
        </Icon>
      )}
      <StyledInput
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        $fieldState={fieldstate}
        disabled={fieldstate === 'disabled'}
        readOnly={fieldstate === 'readonly'}
        type={type}
        value={value}
        $iconPosition={iconPosition}
        ref={inputRef}
      />
    </IconWrapper>
  );
};

export default TextField;
